import { useEffect, useState, type RefObject } from "react";

const LIGHT_SECTION_SELECTOR =
  ".has-hellblau-bg, .has-hellgrau-bg, footer.footer";

/**
 * Returns true while the center of the referenced (fixed) element is located
 * above a section with a light secondary background or the footer.
 */
const useIsOverLightSection = (
  elementRef: RefObject<HTMLElement>,
  isEnabled: boolean,
) => {
  const [isOverLightSection, setIsOverLightSection] = useState(false);

  useEffect(() => {
    if (!isEnabled) return;

    let frameId = 0;

    const check = () => {
      frameId = 0;
      const element = elementRef.current;
      if (!element) return;

      const { left, top, width, height } = element.getBoundingClientRect();
      const elementsAtPoint = document.elementsFromPoint(
        left + width / 2,
        top + height / 2,
      );
      const isOver = elementsAtPoint.some((el) =>
        el.matches(LIGHT_SECTION_SELECTOR),
      );
      setIsOverLightSection(isOver);
    };

    const scheduleCheck = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(check);
    };

    check();
    window.addEventListener("scroll", scheduleCheck, { passive: true });
    window.addEventListener("resize", scheduleCheck);

    return () => {
      window.removeEventListener("scroll", scheduleCheck);
      window.removeEventListener("resize", scheduleCheck);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [elementRef, isEnabled]);

  return isOverLightSection;
};

export default useIsOverLightSection;
